<template>
  <Section id="cases" tone="default" pad-y="xl" aria-labelledby="cases-heading">
    <Container>
      <div class="mb-10 flex flex-col gap-3">
        <p class="text-xs font-semibold tracking-[0.14em] text-accent-strong uppercase">
          {{ t('sections.cases.eyebrow') }}
        </p>
        <h2 id="cases-heading" class="text-3xl font-bold text-ink-strong md:text-4xl">
          {{ t('sections.cases.title') }}
        </h2>
        <p class="max-w-2xl text-base text-ink-muted md:text-lg">
          {{ t('sections.cases.subtitle') }}
        </p>
      </div>

      <CaseCarousel :cases="slides" />
    </Container>
  </Section>
</template>

<script setup lang="ts">
/**
 * `HomeCases` — секция «Кейсы» с автолистающимся слайдером.
 *
 * 14 реальных постеров из `public/cases/case_*.jpg` (`CASES`
 * из `@/content/home-data`). Каждая карточка — брендовый фон + лого +
 * eyebrow + заголовок проекта + короткое описание + сам постер.
 * Слайдер ([CaseCarousel](@/components/domain/CaseCarousel.vue))
 * показывает 1 карточку на mobile / 2 на md+, листает по одной каждые
 * 10 секунд и зацикливается через клоны.
 *
 * Локализация решается здесь: `pickLocalized` для всех текстовых полей,
 * сам слайдер принимает уже локализованный view-model.
 */
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import Container from '@/components/primitives/Container.vue';
import Section from '@/components/primitives/Section.vue';
import CaseCarousel, { type CaseSlideViewModel } from '@/components/domain/CaseCarousel.vue';
import { useLocale } from '@/composables/useLocale';
import { CASES } from '@/content/home-data';
import { pickLocalized } from '@/lib/i18n/localize';

const { t } = useI18n();
const { locale } = useLocale();

const slides = computed<readonly CaseSlideViewModel[]>(() =>
  CASES.map((c) => ({
    id: c.id,
    client: pickLocalized(c, 'client', locale.value),
    eyebrow: pickLocalized(c, 'eyebrow', locale.value),
    title: pickLocalized(c, 'title', locale.value),
    description: pickLocalized(c, 'description', locale.value),
    image: c.image,
    imageAlt: pickLocalized(c, 'imageAlt', locale.value),
    accent: c.accent,
    accentInk: c.accentInk,
  })),
);
</script>
